import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { EmpresaApiService } from '../services/empresa-api.service';
import { CompanyStateLeadApiService } from '../services/company-state-lead-api.service';
import { CompanySectorApiService } from '../services/company-sector-api.service';
import { CompanyRelationshipTypeApiService } from '../services/company-relationship-type-api.service';
import { CompanySizeApiService } from '../services/company-size-api.service';
import { CompanyCycleApiService } from '../services/company-cycle-api.service';
import { ContactCompanyApiService } from '../services/contact-company-api.service';
import { DealDrawerService } from '../../deals/services/deal-drawer.service';
import { LeadDrawerService } from '../../leads/services/lead-drawer.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { EmpresaDetailModalComponent } from '../empresa-detail-modal/empresa-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  CompanyResponse,
  CompanyRequest,
  CompanyStateLeadResponse,
  CompanySector,
  CompanyRelationshipType,
  CompanySize,
  CompanyCycleResponse,
  CompanyTaxType,
  ContactCompanyResponse,
} from '../models/empresa.model';
import { CompanyTaxTypeApiService } from '../services/company-tax-type-api.service';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type ViewMode = 'stateLead' | 'sector' | 'relationshipType' | 'cycle';

interface KanbanColumn {
  id: number;
  name: string;
  color: string;
  companies: CompanyResponse[];
}

interface FilterState {
  searchTerm: string;
  idCompanyStateLead?: number;
  idCompanySector?: number;
  idCompanyRelationshipType?: number;
  idCompanySize?: number;
  idCompanyCycle?: number;
  phonePrefix?: string;
}

@Component({
  selector: 'app-empresa-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    BadgeComponent,
    EmpresaDetailModalComponent,
    CountrySelectComponent,
  ],
  templateUrl: './empresa-kanban.component.html',
  styleUrl: './empresa-kanban.component.css',
})
export class EmpresaKanbanComponent implements OnInit, OnDestroy {
  // inc37 — Ciclo es la primera sección y, como en Contactos y Leads, la agrupación
  // por defecto. El modo NO se persiste, así que el cambio solo afecta a la vista inicial.
  viewMode: ViewMode = 'cycle';
  columns: KanbanColumn[] = [];
  loading = false;
  error = '';

  /** Código ISO (minúscula) del país derivado del teléfono, para la bandera flag-icons. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }
  /** Nombre del país derivado del teléfono (tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país (por prefijo telefónico). */
  readonly filterCountries = COUNTRIES_DATA;
  filtroPais = '';
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.filters.phonePrefix = code
      ? COUNTRIES_DATA.find(c => c.code === code)?.dialCode || undefined
      : undefined;
    this.applyFilters();
  }

  // Catálogos
  stateLeads: CompanyStateLeadResponse[] = [];
  sectors: CompanySector[] = [];
  relationshipTypes: CompanyRelationshipType[] = [];
  sizes: CompanySize[] = [];
  companyCycles: CompanyCycleResponse[] = [];
  taxTypes: CompanyTaxType[] = [];

  // Filtro de responsable
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_empresas';
  filtroOwner: 'all' | 'mine' =
    localStorage.getItem(this.OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all';

  // Filtros
  filters: FilterState = {
    searchTerm: '',
    idCompanyStateLead: undefined,
    idCompanySector: undefined,
    idCompanyRelationshipType: undefined,
    idCompanySize: undefined,
    idCompanyCycle: undefined,
  };
  showFilters = false;

  // Modal de detalle
  showDetailModal = false;
  selectedCompany: CompanyResponse | null = null;

  // Trigger del ciclo de empresa (CREATE_DEAL / CREATE_LEAD al mover la tarjeta de columna)
  private triggerCompany: CompanyResponse | null = null;
  private triggerTargetColumn: KanbanColumn | null = null;
  private triggerType: 'CREATE_DEAL' | 'CREATE_LEAD' | null = null;
  private readonly destroy$ = new Subject<void>();

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'empresa-kanban-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  constructor(
    private empresaApi: EmpresaApiService,
    private stateLeadApi: CompanyStateLeadApiService,
    private sectorApi: CompanySectorApiService,
    private relationshipTypeApi: CompanyRelationshipTypeApiService,
    private sizeApi: CompanySizeApiService,
    private cycleApi: CompanyCycleApiService,
    private contactCompanyApi: ContactCompanyApiService,
    private dealDrawerService: DealDrawerService,
    private leadDrawerService: LeadDrawerService,
    private taxApi: CompanyTaxTypeApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadCatalogs();

    // Al llegar con ?openCompany=<id> (buscador global del header, enlaces desde
    // otros módulos) se abre su modal de detalle sobre el kanban. El detalle de una
    // empresa es un modal, no una ruta propia — mismo patrón que ?openDeal= en
    // negocios, ?openLead= en leads y ?openTicket= en tickets.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const raw = params.get('openCompany');
        const id = raw ? Number(raw) : NaN;
        if (!Number.isNaN(id) && id > 0) this.abrirDetallePorId(id);
      });

    // CREATE_DEAL / CREATE_LEAD: recién cuando el negocio o el lead se guarda se
    // persiste el cambio de ciclo.
    this.dealDrawerService.dealSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onTriggerSaved('CREATE_DEAL'));
    this.leadDrawerService.leadSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onTriggerSaved('CREATE_LEAD'));

    // Si el drawer se cierra sin guardar, el movimiento se revierte recargando.
    this.dealDrawerService.state
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.onDrawerStateChange('CREATE_DEAL', state.isOpen));
    this.leadDrawerService.state
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.onDrawerStateChange('CREATE_LEAD', state.isOpen));
  }

  private onTriggerSaved(type: 'CREATE_DEAL' | 'CREATE_LEAD'): void {
    if (this.triggerType !== type || !this.triggerCompany || !this.triggerTargetColumn) return;
    const company = this.triggerCompany;
    const target = this.triggerTargetColumn;
    this.resetTrigger();
    this.updateCompanyField(company, target, undefined, true);
  }

  private onDrawerStateChange(type: 'CREATE_DEAL' | 'CREATE_LEAD', isOpen: boolean): void {
    if (isOpen || this.triggerType !== type || !this.triggerCompany) return;
    setTimeout(() => {
      if (this.triggerType === type && this.triggerCompany) {
        this.resetTrigger();
        this.applyFilters();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetTrigger(): void {
    this.triggerCompany = null;
    this.triggerTargetColumn = null;
    this.triggerType = null;
  }

  /**
   * Abre el drawer de negocio o de lead prellenado con la empresa y, si la tiene,
   * con su contacto principal (mismo prellenado que hace el kanban de contactos).
   */
  private openTriggerDrawer(
    company: CompanyResponse,
    type: 'CREATE_DEAL' | 'CREATE_LEAD',
  ): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    const openWith = (contactId?: number, contactName?: string) => {
      const opts = {
        companyId: company.id,
        companyName: company.commercialName,
        contactId,
        contactName,
      };
      if (type === 'CREATE_DEAL') this.dealDrawerService.open(opts);
      else this.leadDrawerService.open(opts);
    };

    if (!company.id) {
      openWith();
      return;
    }

    this.contactCompanyApi.getByCompany(company.id, businessId).subscribe({
      next: (associations: ContactCompanyResponse[]) => {
        const primary = associations.find((a) => a.isPrimary) || associations[0];
        const contact = primary?.contact;
        if (!primary) {
          openWith();
          return;
        }
        const name = contact
          ? `${contact.fullname ?? ''}${contact.lastname ? ' ' + contact.lastname : ''}`.trim()
          : undefined;
        openWith(contact?.id ?? primary.idContact, name || undefined);
      },
      error: () => openWith(),
    });
  }

  private loadCatalogs(): void {
    this.loading = true;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    Promise.all([
      this.stateLeadApi.getAll().toPromise(),
      this.sectorApi.getAll().toPromise(),
      this.relationshipTypeApi.getAll().toPromise(),
      this.sizeApi.getAll().toPromise(),
      this.cycleApi.getAll(businessId).toPromise(),
      this.taxApi.getAll().toPromise(),
    ])
      .then(([stateLeads, sectors, relationshipTypes, sizes, cycles, taxs]) => {
        this.stateLeads = stateLeads?.content || [];
        this.sectors = sectors || [];
        this.relationshipTypes = relationshipTypes?.content || [];
        this.sizes = sizes?.content || [];
        this.companyCycles = cycles?.content || [];
        this.taxTypes = taxs?.content || [];

        this.initializeColumns();
        this.loading = false;
      })
      .catch((err) => {
        this.error = 'Error al cargar catálogos';
        this.loading = false;
        console.error('Error:', err);
      });
  }

  private initializeColumns(): void {
    this.columns = [];

    switch (this.viewMode) {
      case 'stateLead':
        this.buildStateLeadColumns();
        break;
      case 'sector':
        this.buildSectorColumns();
        break;
      case 'relationshipType':
        this.buildRelationshipTypeColumns();
        break;
      case 'cycle':
        this.buildCycleColumns();
        break;
    }

    this.loadPage(0);
  }

  private buildStateLeadColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-yellow-500',
      'bg-orange-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
    ];

    this.columns = this.stateLeads.map((stateLead, index) => ({
      id: stateLead.id,
      name: stateLead.name,
      color: colors[index % colors.length],
      companies: [],
    }));

    this.columns.push({
      id: 0,
      name: 'Sin Estado',
      color: 'bg-gray-500',
      companies: [],
    });
  }

  private buildSectorColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-orange-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
    ];

    this.columns = this.sectors.map((sector, index) => ({
      id: sector.id,
      name: sector.name,
      color: colors[index % colors.length],
      companies: [],
    }));

    this.columns.push({
      id: 0,
      name: 'Sin Sector',
      color: 'bg-gray-500',
      companies: [],
    });
  }

  private buildRelationshipTypeColumns(): void {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
    ];

    this.columns = this.relationshipTypes.map((type, index) => ({
      id: type.id,
      name: type.name,
      color: colors[index % colors.length],
      companies: [],
    }));

    this.columns.push({
      id: 0,
      name: 'Sin Tipo',
      color: 'bg-gray-500',
      companies: [],
    });
  }

  private buildCycleColumns(): void {
    const colors = [
      'bg-violet-500',
      'bg-cyan-500',
      'bg-teal-500',
      'bg-amber-500',
      'bg-rose-500',
      'bg-lime-500',
      'bg-sky-500',
      'bg-fuchsia-500',
    ];

    this.columns = this.companyCycles
      .filter((c) => c.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((cycle, index) => ({
        id: cycle.id,
        name: cycle.name,
        color: colors[index % colors.length],
        companies: [],
      }));

    this.columns.push({
      id: 0,
      name: 'Sin Ciclo',
      color: 'bg-gray-500',
      companies: [],
    });
  }

  private loadPage(page: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const searchParams: any = { businessId, page, size: this.pageSize };

    if (this.filters.searchTerm)
      searchParams.searchTerm = this.filters.searchTerm;
    if (this.filters.phonePrefix) searchParams.phonePrefix = this.filters.phonePrefix;
    if (this.filtroOwner === 'mine') searchParams.userPropietyId = user?.id;

    // Filtros globales excluyendo la dimensión del viewMode actual
    switch (this.viewMode) {
      case 'stateLead':
        if (this.filters.idCompanySector !== undefined)
          searchParams.idCompanySector = this.filters.idCompanySector;
        if (this.filters.idCompanyRelationshipType !== undefined)
          searchParams.idCompanyRelationshipType =
            this.filters.idCompanyRelationshipType;
        if (this.filters.idCompanySize !== undefined)
          searchParams.idCompanySize = this.filters.idCompanySize;
        if (this.filters.idCompanyCycle !== undefined)
          searchParams.idCompanyCycle = this.filters.idCompanyCycle;
        break;
      case 'sector':
        if (this.filters.idCompanyStateLead !== undefined)
          searchParams.idCompanyStateLead = this.filters.idCompanyStateLead;
        if (this.filters.idCompanyRelationshipType !== undefined)
          searchParams.idCompanyRelationshipType =
            this.filters.idCompanyRelationshipType;
        if (this.filters.idCompanySize !== undefined)
          searchParams.idCompanySize = this.filters.idCompanySize;
        if (this.filters.idCompanyCycle !== undefined)
          searchParams.idCompanyCycle = this.filters.idCompanyCycle;
        break;
      case 'relationshipType':
        if (this.filters.idCompanyStateLead !== undefined)
          searchParams.idCompanyStateLead = this.filters.idCompanyStateLead;
        if (this.filters.idCompanySector !== undefined)
          searchParams.idCompanySector = this.filters.idCompanySector;
        if (this.filters.idCompanySize !== undefined)
          searchParams.idCompanySize = this.filters.idCompanySize;
        if (this.filters.idCompanyCycle !== undefined)
          searchParams.idCompanyCycle = this.filters.idCompanyCycle;
        break;
      case 'cycle':
        if (this.filters.idCompanyStateLead !== undefined)
          searchParams.idCompanyStateLead = this.filters.idCompanyStateLead;
        if (this.filters.idCompanySector !== undefined)
          searchParams.idCompanySector = this.filters.idCompanySector;
        if (this.filters.idCompanyRelationshipType !== undefined)
          searchParams.idCompanyRelationshipType =
            this.filters.idCompanyRelationshipType;
        if (this.filters.idCompanySize !== undefined)
          searchParams.idCompanySize = this.filters.idCompanySize;
        break;
    }

    this.columns.forEach((col) => (col.companies = []));

    this.empresaApi.searchCompanies(searchParams).subscribe({
      next: (response) => {
        response.content.forEach((company) => {
          const col = this.getColumnForCompany(company);
          if (col) col.companies.push(company);
        });
        this.globalPage = response.number ?? page;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar empresas';
        this.loading = false;
      },
    });
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.loadPage(page);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.applyFilters();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  // Determina a qué columna pertenece una empresa según el viewMode actual
  private getColumnForCompany(
    company: CompanyResponse,
  ): KanbanColumn | undefined {
    let fieldId: number | undefined;

    switch (this.viewMode) {
      case 'stateLead':
        fieldId = company.idCompanyStateLead;
        break;
      case 'sector':
        fieldId = company.idCompanySector;
        break;
      case 'relationshipType':
        fieldId = company.idCompanyRelationshipType;
        break;
      case 'cycle':
        fieldId = company.idCompanyCycle;
        break;
    }

    // Si no tiene valor, va a la columna "Sin ..."  (id = 0)
    return this.columns.find((col) => col.id === (fieldId || 0));
  }

  changeViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.initializeColumns();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  setOwnerFilter(value: 'all' | 'mine'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.applyFilters();
  }

  applyFilters(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.loadPage(0);
  }

  clearFilters(): void {
    this.filtroOwner = 'all';
    this.filters = {
      searchTerm: '',
      idCompanyStateLead: undefined,
      idCompanySector: undefined,
      idCompanyRelationshipType: undefined,
      idCompanySize: undefined,
      idCompanyCycle: undefined,
      phonePrefix: undefined,
    };
    this.filtroPais = '';
    this.applyFilters();
  }

  get availableFilterFields(): {
    stateLead: boolean;
    sector: boolean;
    relationshipType: boolean;
    size: boolean;
    cycle: boolean;
  } {
    return {
      stateLead: this.viewMode !== 'stateLead',
      sector: this.viewMode !== 'sector',
      relationshipType: this.viewMode !== 'relationshipType',
      size: true,
      cycle: this.viewMode !== 'cycle',
    };
  }

  drop(
    event: CdkDragDrop<CompanyResponse[]>,
    targetColumn: KanbanColumn,
  ): void {
    const company = event.previousContainer.data[event.previousIndex];

    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      const sourceColumn = this.columns.find(
        (col) => col.companies === event.previousContainer.data,
      );

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      this.updateCompanyField(company, targetColumn, sourceColumn);
    }
  }

  private updateCompanyField(
    company: CompanyResponse,
    targetColumn: KanbanColumn,
    sourceColumn?: KanbanColumn,
    skipTrigger = false,
  ): void {
    if (!company.id) return;

    // Acción configurada en el catálogo de ciclos de empresa: si el ciclo destino
    // tiene triggerAction=CREATE_DEAL o CREATE_LEAD, primero se crea ese registro y
    // solo si se guarda se persiste el cambio de ciclo (igual que en contactos).
    if (this.viewMode === 'cycle' && !skipTrigger && targetColumn.id !== 0) {
      const cycle = this.companyCycles.find((c) => c.id === targetColumn.id);
      const trigger = cycle?.triggerAction;
      if (trigger === 'CREATE_DEAL' || trigger === 'CREATE_LEAD') {
        this.triggerCompany = company;
        this.triggerTargetColumn = targetColumn;
        this.triggerType = trigger;
        this.openTriggerDrawer(company, trigger);
        return;
      }
    }

    const updatedCompany: CompanyRequest = {
      commercialName: company.commercialName,
      businessName: company.businessName,
      description: company.description,
      phone: company.phone,
      fiscalAddress: company.fiscalAddress,
      imageUrl: company.imageUrl,
      website: company.website,
      idCompanyTaxType: company.idCompanyTaxType,
      taxCode: company.taxCode,
      idCompanyRelationshipType: company.idCompanyRelationshipType,
      idCompanySize: company.idCompanySize,
      idCompanySector: company.idCompanySector,
      idCompanyStateLead: company.idCompanyStateLead,
      idCompanyCycle: company.idCompanyCycle,
      companyParentId: company.companyParentId,
      businessId: company.businessId,
    };

    switch (this.viewMode) {
      case 'stateLead':
        updatedCompany.idCompanyStateLead =
          targetColumn.id === 0 ? undefined : targetColumn.id;
        company.idCompanyStateLead = updatedCompany.idCompanyStateLead;
        break;
      case 'sector':
        updatedCompany.idCompanySector =
          targetColumn.id === 0 ? undefined : targetColumn.id;
        company.idCompanySector = updatedCompany.idCompanySector;
        break;
      case 'relationshipType':
        updatedCompany.idCompanyRelationshipType =
          targetColumn.id === 0 ? undefined : targetColumn.id;
        company.idCompanyRelationshipType =
          updatedCompany.idCompanyRelationshipType;
        break;
      case 'cycle':
        updatedCompany.idCompanyCycle =
          targetColumn.id === 0 ? undefined : targetColumn.id;
        company.idCompanyCycle = updatedCompany.idCompanyCycle;
        break;
    }

    this.empresaApi.updateCompany(company.id, updatedCompany).subscribe({
      next: () => {
        // El UI ya fue actualizado por transferArrayItem + actualización del objeto en memoria
      },
      error: (err) => {
        console.error('Error al actualizar empresa:', err);
        this.confirmDialog.alert({
          title: 'Error',
          message:
            'Error al actualizar la empresa. Por favor, recargue la página.',
          variant: 'danger',
        });
        this.applyFilters();
      },
    });
  }

  getColumnIds(): string[] {
    return this.columns.map((col) => `col-${col.id}`);
  }

  nuevaEmpresa(): void {
    this.router.navigate(['/empresas/crear']);
  }

  vistaLista(): void {
    this.router.navigate(['/empresas/lista']);
  }

  editarEmpresa(company: CompanyResponse, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/empresas/editar', company.id]);
  }

  async eliminarEmpresa(
    company: CompanyResponse,
    column: KanbanColumn,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();

    if (!company.id) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar empresa',
      message: `¿Está seguro de eliminar la empresa "${company.commercialName}"?`,
    });
    if (!ok) return;
    this.empresaApi.deleteCompany(company.id).subscribe({
      next: () => {
        const index = column.companies.findIndex((c) => c.id === company.id);
        if (index !== -1) {
          column.companies.splice(index, 1);
        }
        this.toast.deleted('Empresa');
      },
      error: (err) => {
        this.manejarErrorEliminar(err);
      },
    });
  }

  /** Inc 8: 409 COMPANY_HAS_ASSOCIATIONS → diálogo informativo con los conteos; otros errores → toast. */
  private manejarErrorEliminar(err: {
    status?: number;
    error?: { message?: string; details?: Record<string, unknown> };
  }): void {
    const details = err?.error?.details;
    if (
      err?.status === 409 &&
      details?.['error'] === 'COMPANY_HAS_ASSOCIATIONS'
    ) {
      const partes: string[] = [];
      const agregar = (count: unknown, singular: string, plural: string) => {
        const n = typeof count === 'number' ? count : 0;
        if (n > 0) partes.push(`${n} ${n === 1 ? singular : plural}`);
      };
      agregar(details['contacts'], 'contacto', 'contactos');
      agregar(details['deals'], 'negociación', 'negociaciones');
      agregar(details['tasks'], 'tarea', 'tareas');
      agregar(details['tickets'], 'ticket', 'tickets');

      void this.confirmDialog.alert({
        title: 'No se puede eliminar la empresa',
        message: `No se puede eliminar la empresa porque tiene: ${partes.join(', ')}. Desvincula estos registros primero.`,
        variant: 'warning',
        confirmText: 'Entendido',
      });
      return;
    }
    this.toast.error({
      title: 'Error',
      message: err?.error?.message || 'Error al eliminar la empresa',
    });
  }

  // Métodos auxiliares para obtener nombres de catálogos
  getStateLeadName(id?: number): string {
    if (!id) return 'Sin estado';
    const stateLead = this.stateLeads.find((s) => s.id === id);
    return stateLead?.name || '—';
  }

  getSectorName(id?: number): string {
    if (!id) return 'Sin sector';
    const sector = this.sectors.find((s) => s.id === id);
    return sector?.name || '—';
  }

  getRelationshipTypeName(id?: number): string {
    if (!id) return 'Sin tipo';
    const type = this.relationshipTypes.find((t) => t.id === id);
    return type?.name || '—';
  }

  getCycleName(id?: number): string {
    if (!id) return '—';
    return this.companyCycles.find((c) => c.id === id)?.name || '—';
  }

  get viewModeTitle(): string {
    switch (this.viewMode) {
      case 'stateLead':
        return 'Por Estado Lead';
      case 'sector':
        return 'Por Sector';
      case 'relationshipType':
        return 'Por Tipo de Relación';
      case 'cycle':
        return 'Por Ciclo';
    }
  }

  verDetalle(empresa: CompanyResponse): void {
    this.selectedCompany = empresa;
    this.showDetailModal = true;
  }

  /**
   * Carga la empresa por id y abre su modal de detalle. Entrada de ?openCompany=<id>:
   * se pide al backend en vez de buscarla en las columnas para que funcione aunque
   * la empresa no esté en la página cargada del kanban.
   */
  private abrirDetallePorId(id: number): void {
    this.empresaApi
      .getCompanyById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresa) => {
          this.selectedCompany = empresa;
          this.showDetailModal = true;
        },
        error: () => {
          this.toast.error({
            title: 'No se pudo abrir la empresa',
            message: 'La empresa no existe o no tienes acceso.',
          });
        },
      });
  }

  cerrarModalDetalle(): void {
    this.showDetailModal = false;
    this.selectedCompany = null;
    // Limpiar ?openCompany para poder reabrir la misma empresa desde el buscador
    // (navegar dos veces a la misma URL no vuelve a emitir el query param).
    if (this.route.snapshot.queryParamMap.has('openCompany')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openCompany: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }
}
